import React, { FC } from "react";

import { ExperienceDto } from "@intelligems/ig-types";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";
import { Grid } from "@mui/material";

import { useConfiguration } from "../../../../api/configurations/use-configuration";
import {
  getNewestEndedTest,
  getOldestStartedTest,
  noDecimalCurrencyFormatter,
  percentFormatter,
} from "../../../../api/formatters/dashboard";
import { RevenueIcon } from "../../../../images/Icons/RevenueIcon";
import { RevenueUpliftIcon } from "../../../../images/Icons/RevenueUpliftIcon";
import { TestDaysIcon } from "../../../../images/Icons/TestDaysIcon";
import { getDaysBetweenDates } from "../../../../utils/dateConverters";

import { MetricChip } from "./MetricChip";

interface MetricHeaderProps {
  allImpactExperiments: ExperienceDto[];
  nonControlWinners: ExperienceDto[];
  allExperiments: ExperienceDto[];
  newestEndedTest: ExperienceDto;
}
export const MetricHeader: FC<MetricHeaderProps> = ({
  allImpactExperiments,
  nonControlWinners,
  allExperiments,
  newestEndedTest,
}) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const newestEndedTestWinner = newestEndedTest.variations.find(
    (group) =>
      group.id === newestEndedTest.experienceAnalysis?.impactWinnerVariationId
  );

  const sumMonthlyUpliftAmount = nonControlWinners.reduce(
    (prev, curr) => prev + (curr.experienceAnalysis?.impact || 0),
    0
  );

  const maxMonthly = Math.max(
    ...allImpactExperiments!.map(
      (exp) => exp.experienceAnalysis.controlMonthly!
    )!
  );

  const impactMetric = allImpactExperiments[0].experienceAnalysis?.impactMetric
    ? capitalizeFirstLetter(
        allImpactExperiments[0].experienceAnalysis.impactMetric
      )
    : "";

  const isTestLive = () => {
    const currentTime = Date.now();
    if (allExperiments.filter((exp) => exp.status === "started").length < 1) {
      const newestEndedTest = getNewestEndedTest(allExperiments);
      const days = Math.round(
        getDaysBetweenDates(newestEndedTest!.endedAtTs!, currentTime)
      );
      return {
        title: "Time Since Last Test",
        tooltip: "Days since the end of your most recent test.",
        value: `${days} Day${days === 1 ? "" : "s"}`,
      };
    } else {
      const oldestStartedTest = getOldestStartedTest(allExperiments);
      const days = Math.round(
        getDaysBetweenDates(oldestStartedTest!.startedAtTs!, currentTime)
      );
      return {
        title: "Test Live For",
        tooltip: "Days since start of your live test.",
        value: `${days} Day${days === 1 ? "" : "s"}`,
      };
    }
  };

  const metricChips = [
    {
      title: `Monthly ${impactMetric} Uplift`,
      tooltip:
        "Cumulative monthly uplift of all tests, calculated based on the performance of winning group vs. control.",
      icon: RevenueIcon,
      value1: noDecimalCurrencyFormatter(
        sumMonthlyUpliftAmount,
        false,
        storeCurrencyConfig!.value!
      ),
      value2: percentFormatter(
        sumMonthlyUpliftAmount / maxMonthly,
        false,
        storeCurrencyConfig!.value!
      ),
      footerText: `Based on estimated monthly ${impactMetric} of ${noDecimalCurrencyFormatter(
        maxMonthly,
        false,
        storeCurrencyConfig!.value!
      )}`,
    },
    {
      title: "Latest Test Outcome",
      tooltip:
        "Estimated monthly impact. Calculated based on winning group vs. control (uplift) or, in the case where control was the winning group, control vs. the second-highest performing group (loss avoidance).",
      icon: RevenueUpliftIcon,
      value1: noDecimalCurrencyFormatter(
        newestEndedTest.experienceAnalysis.impact!,
        false,
        storeCurrencyConfig!.value!
      ),
      footerText: newestEndedTestWinner?.isControl
        ? "Loss Avoidance"
        : "Uplift",
    },
    {
      title: isTestLive().title,
      tooltip: isTestLive().tooltip,
      icon: TestDaysIcon,
      value1: isTestLive().value,
    },
  ];

  if (newestEndedTestWinner) {
    return (
      <Grid
        container
        item
        direction={"column"}
        rowSpacing={2}
        columnSpacing={2}
      >
        {metricChips.map((chip) => (
          <Grid item key={chip.title} xs={12} md={6} lg={6}>
            <MetricChip
              title={chip.title}
              tooltip={chip.tooltip}
              icon={chip.icon}
              value1={chip.value1}
              value2={chip.value2}
              footerText={chip.footerText}
            />
          </Grid>
        ))}
      </Grid>
    );
  }
  return null;
};
